/**
 * ExpenseLog — Shared expense tracking with dual-parent approval workflow
 * Features: receipt upload (camera/gallery), approve/reject with notes,
 * Apple Pay + Venmo deep links, Ledger view, status pipeline.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import { data as api } from '@/utils/auth'
import { printExpenseLog } from '@/utils/printUtils'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
}
function groupByDate(expenses) {
  const groups = {}
  for (const e of expenses) {
    const label = fmtDate(e.purchase_date || e.created_at)
    if (!groups[label]) groups[label] = []
    groups[label].push(e)
  }
  return groups
}

const CATEGORIES = [
  { id: 'medical',        label: 'Medical',    emoji: '🏥' },
  { id: 'education',      label: 'Education',  emoji: '📚' },
  { id: 'extracurricular',label: 'Activities', emoji: '⚽' },
  { id: 'clothing',       label: 'Clothing',   emoji: '👕' },
  { id: 'food',           label: 'Food',       emoji: '🍽️' },
  { id: 'childcare',      label: 'Childcare',  emoji: '🧸' },
  { id: 'transport',      label: 'Transport',  emoji: '🚗' },
  { id: 'other',          label: 'Other',      emoji: '📎' },
]
const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.id, c]))

const STATUS_META = {
  pending:  { label: 'Needs Review', color: 'text-amber-700',  bg: 'bg-amber-50'  },
  approved: { label: 'Approved',     color: 'text-green-700',  bg: 'bg-green-100' },
  rejected: { label: 'Declined',     color: 'text-red-600',    bg: 'bg-red-100'   },
  paid:     { label: 'Paid',         color: 'text-brand-700',  bg: 'bg-blue-50'   },
}

// ── Receipt Picker ────────────────────────────────────────────────────────────
function ReceiptPicker({ onUploaded }) {
  const cameraRef   = useRef()
  const galleryRef  = useRef()
  const [preview, setPreview]     = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError]         = useState('')

  async function handleFile(file) {
    if (!file) return
    setError('')
    const reader = new FileReader()
    reader.onload = ev => setPreview(ev.target.result)
    reader.readAsDataURL(file)
    setUploading(true)
    try {
      const saved = await api.uploadFile(file)
      onUploaded(`/api/data/files?id=${saved.id}`)
    } catch {
      setError('Upload failed — try again.')
      setPreview(null)
    } finally {
      setUploading(false)
    }
  }

  if (preview) {
    return (
      <div className="mb-4">
        <div className="relative rounded-xl overflow-hidden border border-slate-200">
          <img src={preview} alt="Receipt" className="w-full object-cover max-h-40" />
          <button
            onClick={() => { setPreview(null); onUploaded(null) }}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center text-sm font-bold"
          >×</button>
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <p className="text-xs text-slate-600 font-semibold">Uploading…</p>
            </div>
          )}
        </div>
        {!uploading && <p className="text-xs text-green-600 font-semibold mt-1">✓ Receipt attached</p>}
      </div>
    )
  }

  return (
    <div className="mb-4">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">
        Receipt (optional)
      </label>
      <div className="flex gap-2">
        <button type="button" onClick={() => cameraRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-sm font-semibold active:bg-slate-50"
        >📷 Take Photo</button>
        <button type="button" onClick={() => galleryRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-sm font-semibold active:bg-slate-50"
        >🖼 Upload File</button>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" className="hidden"
        onChange={e => handleFile(e.target.files[0])} />
      <input ref={galleryRef} type="file" accept="image/*,application/pdf" className="hidden"
        onChange={e => handleFile(e.target.files[0])} />
    </div>
  )
}

// ── Add Expense Modal ─────────────────────────────────────────────────────────
function AddExpenseModal({ onClose, onSave }) {
  const { children: kidProfiles } = useApp()
  const [form, setForm] = useState({
    title: '', category: 'education', total_amount: '',
    payer_pct: 50,
    purchase_date: new Date().toISOString().slice(0, 10),
    notes: '', child_id: '', receipt_url: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const total        = parseFloat(form.total_amount) || 0
  const myShare      = total * Number(form.payer_pct) / 100
  const partnerShare = total * (100 - Number(form.payer_pct)) / 100

  async function handleSave() {
    if (!form.title.trim())               return setError('Title is required.')
    if (!form.total_amount || total <= 0) return setError('Enter a valid amount.')
    setSaving(true); setError('')
    try {
      await onSave({ ...form, total_amount: total, payer_pct: Number(form.payer_pct) })
      onClose()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-y-auto"
        style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}
      >
        {/* Sticky header with X */}
        <div className="sticky top-0 bg-white pt-3 pb-2 px-5 z-10 border-b border-slate-100">
          <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3" />
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-800">Log Expense</h2>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-lg font-bold active:bg-slate-200"
            >×</button>
          </div>
        </div>

        <div className="px-5 pb-8 pt-4">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Expense Title *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)}
            placeholder="e.g. Piano Lessons, Dentist Visit"
            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-brand-500 mb-4"
          />

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">Category</label>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => set('category', c.id)}
                className={`flex flex-col items-center gap-1 py-2 rounded-xl border-2 text-xs font-medium transition-all ${
                  form.category === c.id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500'
                }`}
              >
                <span className="text-lg">{c.emoji}</span>
                <span className="leading-tight text-center">{c.label}</span>
              </button>
            ))}
          </div>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Total Amount *</label>
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden mb-4 focus-within:border-brand-500">
            <span className="px-3 text-slate-500 font-semibold text-sm">$</span>
            <input type="number" min="0" step="0.01"
              value={form.total_amount} onChange={e => set('total_amount', e.target.value)}
              placeholder="0.00"
              className="flex-1 py-3 pr-4 text-sm text-slate-800 outline-none"
            />
          </div>

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">
            Split — Your Share: <strong className="text-brand-700">{form.payer_pct}%</strong>
          </label>
          <input type="range" min={0} max={100} step={5}
            value={form.payer_pct} onChange={e => set('payer_pct', e.target.value)}
            className="w-full mb-1 accent-brand-700"
          />
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>0%</span><span>50/50</span><span>100%</span>
          </div>
          {total > 0 && (
            <div className="flex gap-3 mb-4">
              <div className="flex-1 bg-brand-50 rounded-xl p-3 text-center">
                <p className="text-xs text-brand-700 font-semibold">You Pay</p>
                <p className="text-sm font-bold text-brand-700">{fmtMoney(myShare)}</p>
              </div>
              <div className="flex-1 bg-slate-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500 font-semibold">Co-Parent Owes</p>
                <p className="text-sm font-bold text-slate-700">{fmtMoney(partnerShare)}</p>
              </div>
            </div>
          )}

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Purchase Date</label>
          <input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-brand-500 mb-4"
          />

          {kidProfiles?.length > 0 && (
            <>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">For Child (optional)</label>
              <select value={form.child_id} onChange={e => set('child_id', e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-brand-500 mb-4"
              >
                <option value="">Not specified</option>
                {kidProfiles.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </>
          )}

          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Notes (optional)</label>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
            placeholder="Any context or details…" rows={2}
            className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-brand-500 resize-none mb-4"
          />

          <ReceiptPicker onUploaded={url => set('receipt_url', url || '')} />

          {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

          <button onClick={handleSave} disabled={saving}
            className="w-full py-3.5 rounded-xl bg-brand-700 text-white font-bold text-sm disabled:opacity-50"
          >
            {saving ? 'Saving…' : '📤 Submit Expense'}
          </button>
          <p className="text-center text-xs text-slate-400 mt-2">
            Your co-parent will be notified to review and approve.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Payment Sheet ─────────────────────────────────────────────────────────────
function PaymentSheet({ expense, currentUserId, onMarkPaid }) {
  const isMyExpense = expense.submitted_by === currentUserId
  const amountOwed  = isMyExpense
    ? expense.total_amount * (100 - expense.payer_pct) / 100
    : expense.total_amount * expense.payer_pct / 100
  const [otherMethod, setOtherMethod] = useState('Zelle')
  const [awaitingConfirm, setAwaitingConfirm] = useState(null)
  const OTHER_METHODS = ['Zelle', 'Check / Other', 'Cash', 'OFWpay']

  async function handleApplePay() {
    if (window.PaymentRequest) {
      try {
        const req = new window.PaymentRequest(
          [{ supportedMethods: 'https://apple.com/apple-pay',
             data: { version: 3, merchantIdentifier: 'merchant.goodguardian',
                     merchantCapabilities: ['supports3DS'],
                     supportedNetworks: ['visa','masterCard','amex','discover'],
                     countryCode: 'US' } }],
          { total: { label: `GoodGuardian: ${expense.title}`,
                     amount: { currency: 'USD', value: amountOwed.toFixed(2) } } }
        )
        const canPay = await req.canMakePayment()
        if (canPay) {
          const result = await req.show()
          await result.complete('success')
          onMarkPaid(expense.id, 'Apple Pay')
          return
        }
      } catch (err) {
        if (err?.name === 'AbortError') return
      }
    }
    setAwaitingConfirm('Apple Pay')
  }

  function handleVenmo() {
    const amt  = amountOwed.toFixed(2)
    const note = encodeURIComponent(`GoodGuardian: ${expense.title}`)
    const txn  = isMyExpense ? 'charge' : 'pay'
    const a    = document.createElement('a')
    a.href = `venmo://paycharge?txn=${txn}&amount=${amt}&note=${note}`
    a.click()
    setTimeout(() => setAwaitingConfirm('Venmo'), 1800)
  }

  if (awaitingConfirm) {
    return (
      <div className="mb-3 bg-green-50 border border-green-200 rounded-2xl p-4">
        <p className="text-sm font-semibold text-slate-800 mb-1">Payment completed?</p>
        <p className="text-xs text-slate-500 mb-4">
          {fmtMoney(amountOwed)} via {awaitingConfirm} — confirm to mark this expense as paid.
        </p>
        <div className="flex gap-2">
          <button onClick={() => onMarkPaid(expense.id, awaitingConfirm)}
            className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-bold text-sm">
            ✓ Yes, Paid
          </button>
          <button onClick={() => setAwaitingConfirm(null)}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-medium text-sm">
            Not Yet
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-3">
      <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500 font-medium">{isMyExpense ? 'Co-parent owes you' : 'You owe'}</p>
        <p className="text-lg font-bold text-brand-700">{fmtMoney(amountOwed)}</p>
      </div>

      <button onClick={handleApplePay}
        className="w-full py-3.5 rounded-xl bg-black text-white font-semibold text-sm mb-2.5 flex items-center justify-center gap-2 active:opacity-80"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
        Pay with Apple Pay
      </button>

      <button onClick={handleVenmo}
        className="w-full py-3.5 rounded-xl text-white font-semibold text-sm mb-4 flex items-center justify-center gap-2 active:opacity-80"
        style={{ backgroundColor: '#3D95CE' }}
      >
        <span className="text-base font-black">V</span>
        {isMyExpense ? 'Request via Venmo' : 'Pay with Venmo'}
      </button>

      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Or record payment via</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {OTHER_METHODS.map(m => (
          <button key={m} onClick={() => setOtherMethod(m)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
              otherMethod === m ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500'
            }`}
          >{m}</button>
        ))}
      </div>
      <button onClick={() => onMarkPaid(expense.id, otherMethod)}
        className="w-full py-3 rounded-xl bg-brand-700 text-white font-bold text-sm"
      >✓ Mark Paid via {otherMethod}</button>
    </div>
  )
}

// ── Expense Detail Sheet ──────────────────────────────────────────────────────
function ExpenseDetail({ expense, currentUserId, onClose, onAction, onMarkPaid }) {
  const cat          = CAT_MAP[expense.category] || CAT_MAP.other
  const status       = STATUS_META[expense.status] || STATUS_META.pending
  const myShare      = expense.total_amount * expense.payer_pct / 100
  const partnerShare = expense.total_amount * (100 - expense.payer_pct) / 100
  const isMyExpense  = expense.submitted_by === currentUserId
  const canApprove   = !isMyExpense && expense.status === 'pending'
  const canPay       = expense.status === 'approved'
  const [actionNote, setActionNote] = useState('')
  const [actioning, setActioning]   = useState(false)

  async function doAction(act) {
    setActioning(true)
    await onAction(expense.id, act, actionNote.trim() || undefined)
    setActioning(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-y-auto"
        style={{ maxHeight: '92vh' }} onClick={e => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 bg-white pt-3 pb-2 px-5 border-b border-slate-100 z-10">
          <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{cat.emoji}</span>
              <div>
                <h2 className="text-base font-bold text-slate-800 leading-tight">{expense.title}</h2>
                <p className="text-xs text-slate-500">{cat.label} · {fmtDate(expense.purchase_date)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.bg} ${status.color}`}>
                {status.label}
              </span>
              <button onClick={onClose}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-lg font-bold active:bg-slate-200">
                ×
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 pb-8 pt-4">
          {/* Status timeline */}
          <div className="flex items-center gap-1 mb-5">
            {[
              { key: 'pending',  icon: '📤', label: 'Submitted' },
              { key: 'approved', icon: '✅', label: 'Approved'  },
              { key: 'paid',     icon: '💰', label: 'Paid'      },
            ].map((step, i) => {
              const ORDER     = ['pending','approved','paid']
              const safeStatus = expense.status === 'rejected' ? 'pending' : expense.status
              const currentIdx = ORDER.indexOf(safeStatus)
              const stepIdx    = ORDER.indexOf(step.key)
              const done       = currentIdx >= stepIdx
              const active     = safeStatus === step.key
              return (
                <div key={step.key} className="flex items-center flex-1 min-w-0">
                  <div className={`flex flex-col items-center flex-1 ${done ? 'opacity-100' : 'opacity-30'}`}>
                    <span className="text-lg">{step.icon}</span>
                    <p className={`text-xs font-semibold mt-0.5 ${active ? 'text-brand-700' : 'text-slate-500'}`}>
                      {step.label}
                    </p>
                  </div>
                  {i < 2 && (
                    <div className={`h-0.5 flex-1 rounded-full mx-1 ${currentIdx > i ? 'bg-brand-500' : 'bg-slate-200'}`} />
                  )}
                </div>
              )
            })}
          </div>

          {expense.status === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
              <span>🚫</span>
              <p className="text-sm text-red-700 font-semibold">This expense was declined.</p>
            </div>
          )}

          {/* Amounts */}
          <div className="bg-slate-50 rounded-2xl p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-500">Total</span>
              <span className="text-base font-bold text-slate-800">{fmtMoney(expense.total_amount)}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-slate-500">Split</span>
              <span className="text-sm font-semibold text-slate-700">{expense.payer_pct}/{100 - expense.payer_pct}</span>
            </div>
            <div className="border-t border-slate-200 pt-3 flex gap-3">
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 mb-0.5">Submitter Pays</p>
                <p className="text-base font-bold text-brand-700">{fmtMoney(myShare)}</p>
              </div>
              <div className="w-px bg-slate-200" />
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 mb-0.5">Co-Parent Pays</p>
                <p className="text-base font-bold text-orange-500">{fmtMoney(partnerShare)}</p>
              </div>
            </div>
          </div>

          {expense.notes && (
            <div className="bg-slate-50 rounded-xl p-3 mb-4">
              <p className="text-xs text-slate-500 font-semibold mb-1">Notes</p>
              <p className="text-sm text-slate-700">{expense.notes}</p>
            </div>
          )}

          {/* Receipt */}
          {expense.receipt_url && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Receipt</p>
              <a href={expense.receipt_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 active:bg-slate-100"
              >
                <span className="text-2xl">📎</span>
                <div>
                  <p className="text-sm font-semibold text-brand-700">View Receipt</p>
                  <p className="text-xs text-slate-500">Tap to open</p>
                </div>
              </a>
            </div>
          )}

          {expense.payment_method && (
            <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-4 py-2.5 mb-4">
              <span>💳</span>
              <p className="text-sm text-brand-700 font-semibold">Paid via {expense.payment_method}</p>
            </div>
          )}

          <p className="text-xs text-slate-400 mb-5">
            Submitted by <strong className="text-slate-600">
              {expense.submitted_by_name || (isMyExpense ? 'You' : 'Co-Parent')}
            </strong> · {fmtDate(expense.created_at)}
          </p>

          {/* Approve / Decline with note */}
          {canApprove && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">
                Add a Note (optional)
              </label>
              <textarea
                value={actionNote} onChange={e => setActionNote(e.target.value)}
                placeholder="e.g. Approved ✓  or  Declined — need original receipt"
                rows={2}
                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-800 outline-none focus:border-brand-500 resize-none mb-3"
              />
              <div className="flex gap-3">
                <button onClick={() => doAction('approve')} disabled={actioning}
                  className="flex-1 py-3 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-50"
                >✓ Approve</button>
                <button onClick={() => doAction('reject')} disabled={actioning}
                  className="flex-1 py-3 rounded-xl border-2 border-red-300 text-red-600 font-bold text-sm disabled:opacity-50"
                >✗ Decline</button>
              </div>
            </div>
          )}

          {/* Payment */}
          {canPay && (
            <PaymentSheet expense={expense} currentUserId={currentUserId} onMarkPaid={onMarkPaid} />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Expense Card ──────────────────────────────────────────────────────────────
function ExpenseCard({ expense, currentUserId, onClick, onQuickPayApple, onQuickPayVenmo }) {
  const cat          = CAT_MAP[expense.category] || CAT_MAP.other
  const status       = STATUS_META[expense.status] || STATUS_META.pending
  const isMyExpense  = expense.submitted_by === currentUserId
  const owesAmt      = isMyExpense
    ? expense.total_amount * (100 - expense.payer_pct) / 100
    : expense.total_amount * expense.payer_pct / 100
  const owesLabel    = isMyExpense ? 'Co-Parent owes' : 'You owe'
  const owesColor    = isMyExpense ? 'text-orange-500' : 'text-purple-600'
  const canQuickPay  = !isMyExpense && expense.status === 'approved'

  return (
    <div className="bg-white border-b border-slate-100">
      <button onClick={onClick}
        className="w-full flex items-start gap-3 px-4 pt-4 pb-3 active:bg-slate-50 text-left"
      >
        <span className="text-2xl mt-0.5 flex-shrink-0">{cat.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="font-semibold text-base text-slate-800 truncate">{expense.title}</p>
            {expense.receipt_url && <span className="text-slate-400 text-sm">📎</span>}
          </div>
          <p className="text-sm text-slate-500">
            {isMyExpense ? 'You' : 'Co-Parent'} · {fmtDate(expense.purchase_date)}
          </p>
          <p className="text-sm text-slate-500">
            {cat.label} ({expense.payer_pct}/{100 - expense.payer_pct}) · {fmtMoney(expense.total_amount)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0 ml-2">
          <div className="text-right">
            <p className={`text-xs font-semibold ${owesColor}`}>{owesLabel}</p>
            <p className={`text-base font-bold ${owesColor}`}>{fmtMoney(owesAmt)}</p>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>
            {expense.status === 'paid' && expense.payment_method
              ? `Paid · ${expense.payment_method}`
              : status.label}
          </span>
        </div>
      </button>

      {/* Inline pay buttons — only for approved expenses the current user owes */}
      {canQuickPay && (
        <div className="flex gap-2 px-4 pb-3">
          <button
            onClick={e => { e.stopPropagation(); onQuickPayApple?.(expense) }}
            className="flex-1 py-2.5 rounded-xl bg-black text-white text-xs font-bold flex items-center justify-center gap-1.5 active:opacity-75"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Pay · Apple Pay
          </button>
          <button
            onClick={e => { e.stopPropagation(); onQuickPayVenmo?.(expense) }}
            className="flex-1 py-2.5 rounded-xl text-white text-xs font-bold flex items-center justify-center gap-1.5 active:opacity-75"
            style={{ backgroundColor: '#3D95CE' }}
          >
            <span className="font-black text-sm">V</span>
            Pay · Venmo
          </button>
        </div>
      )}
    </div>
  )
}

// ── Balance Summary Bar ───────────────────────────────────────────────────────
function SummaryBar({ expenses, currentUserId }) {
  const { youOwe, theyOwe } = expenses.reduce((acc, e) => {
    if (!['pending','approved'].includes(e.status)) return acc
    const isMyExpense = e.submitted_by === currentUserId
    if (isMyExpense) acc.theyOwe += e.total_amount * (100 - e.payer_pct) / 100
    else             acc.youOwe  += e.total_amount * e.payer_pct / 100
    return acc
  }, { youOwe: 0, theyOwe: 0 })
  const net = theyOwe - youOwe

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-100 flex-shrink-0">
      <div className="flex-1 text-center">
        <p className="text-xs text-slate-500 font-semibold">You Owe</p>
        <p className="text-base font-bold text-purple-600">{fmtMoney(youOwe)}</p>
      </div>
      <div className="w-px h-8 bg-slate-200" />
      <div className="flex-1 text-center">
        <p className="text-xs text-slate-500 font-semibold">They Owe</p>
        <p className="text-base font-bold text-orange-500">{fmtMoney(theyOwe)}</p>
      </div>
      <div className="w-px h-8 bg-slate-200" />
      <div className="flex-1 text-center">
        <p className="text-xs text-slate-500 font-semibold">Net</p>
        <p className={`text-base font-bold ${net >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {net >= 0 ? '+' : ''}{fmtMoney(Math.abs(net))}
        </p>
      </div>
    </div>
  )
}

// ── Owed Banner ───────────────────────────────────────────────────────────────
function OwedBanner({ owedExpenses, onMarkPaid }) {
  const totalOwed = owedExpenses.reduce((s, e) => s + e.total_amount * e.payer_pct / 100, 0)
  const [confirm, setConfirm] = useState(null)

  if (!owedExpenses.length || totalOwed <= 0) return null

  async function handleApplePay() {
    if (window.PaymentRequest) {
      try {
        const req = new window.PaymentRequest(
          [{ supportedMethods: 'https://apple.com/apple-pay',
             data: { version: 3, merchantIdentifier: 'merchant.goodguardian',
                     merchantCapabilities: ['supports3DS'],
                     supportedNetworks: ['visa','masterCard','amex','discover'],
                     countryCode: 'US' } }],
          { total: { label: `GoodGuardian: ${owedExpenses.length} shared expense${owedExpenses.length > 1 ? 's' : ''}`,
                     amount: { currency: 'USD', value: totalOwed.toFixed(2) } } }
        )
        if (await req.canMakePayment()) {
          const result = await req.show()
          await result.complete('success')
          for (const e of owedExpenses) await onMarkPaid(e.id, 'Apple Pay')
          return
        }
      } catch (err) { if (err?.name === 'AbortError') return }
    }
    setConfirm({ method: 'Apple Pay' })
  }

  function handleVenmo() {
    const note = encodeURIComponent(`GoodGuardian: ${owedExpenses.length} shared expense${owedExpenses.length > 1 ? 's' : ''}`)
    const a = document.createElement('a')
    a.href = `venmo://paycharge?txn=pay&amount=${totalOwed.toFixed(2)}&note=${note}`
    a.click()
    setTimeout(() => setConfirm({ method: 'Venmo' }), 1800)
  }

  async function confirmAll() {
    for (const e of owedExpenses) await onMarkPaid(e.id, confirm.method)
    setConfirm(null)
  }

  if (confirm) {
    return (
      <div className="mx-4 mt-3 bg-green-50 border border-green-200 rounded-2xl p-4">
        <p className="text-sm font-bold text-slate-800 mb-1">Payment sent via {confirm.method}?</p>
        <p className="text-xs text-slate-500 mb-4">
          {fmtMoney(totalOwed)} for {owedExpenses.length} expense{owedExpenses.length > 1 ? 's' : ''}
        </p>
        <div className="flex gap-2">
          <button onClick={confirmAll}
            className="flex-1 py-2.5 rounded-xl bg-green-600 text-white font-bold text-sm">
            ✓ Yes, Mark Paid
          </button>
          <button onClick={() => setConfirm(null)}
            className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium">
            Not Yet
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-4 mt-3">
      <div className="bg-gradient-to-r from-brand-700 to-brand-500 rounded-2xl p-4 shadow-md">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-white/75 text-xs font-semibold uppercase tracking-wider">You Owe Co-Parent</p>
            <p className="text-white text-3xl font-extrabold leading-tight">{fmtMoney(totalOwed)}</p>
            <p className="text-white/60 text-xs mt-0.5">
              {owedExpenses.length} approved expense{owedExpenses.length !== 1 ? 's' : ''} · tap below to pay
            </p>
          </div>
          <span className="text-4xl">💳</span>
        </div>
        <div className="flex gap-2.5">
          <button onClick={handleApplePay}
            className="flex-1 py-3 rounded-xl bg-black text-white font-bold text-sm flex items-center justify-center gap-2 active:opacity-75">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Pay All · Apple Pay
          </button>
          <button onClick={handleVenmo}
            className="flex-1 py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 active:opacity-75"
            style={{ backgroundColor: '#3D95CE' }}>
            <span className="text-base font-black">V</span>
            Pay All · Venmo
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Ledger View ───────────────────────────────────────────────────────────────
const LEDGER_SECTIONS = [
  { key: 'pending',  label: 'Needs Review', icon: '📤', color: 'text-amber-700', bg: 'bg-amber-50',  border: 'border-amber-200', dot: 'bg-amber-400' },
  { key: 'approved', label: 'Approved',     icon: '✅', color: 'text-green-700', bg: 'bg-green-50',  border: 'border-green-200', dot: 'bg-green-500' },
  { key: 'paid',     label: 'Paid',         icon: '💰', color: 'text-brand-700', bg: 'bg-blue-50',   border: 'border-blue-200',  dot: 'bg-brand-500' },
  { key: 'rejected', label: 'Declined',     icon: '🚫', color: 'text-red-600',   bg: 'bg-red-50',    border: 'border-red-200',   dot: 'bg-red-400'   },
]

function LedgerView({ expenses, currentUserId, onSelectExpense }) {
  const pipeline = ['pending','approved','paid'].map(key => ({
    key,
    label: key === 'pending' ? 'Review' : key.charAt(0).toUpperCase() + key.slice(1),
    total: expenses.filter(e => e.status === key).reduce((s, e) => s + e.total_amount, 0),
    count: expenses.filter(e => e.status === key).length,
  }))

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Payment Pipeline</p>
        <div className="flex items-center">
          {pipeline.map((pt, i) => (
            <div key={pt.key} className="flex items-center flex-1 min-w-0">
              <div className="flex-1 text-center">
                <p className="text-xs text-slate-500 font-semibold mb-1">{pt.label}</p>
                <p className="text-sm font-bold text-slate-800">{fmtMoney(pt.total)}</p>
                <p className="text-xs text-slate-400">{pt.count} item{pt.count !== 1 ? 's' : ''}</p>
              </div>
              {i < pipeline.length - 1 && (
                <div className="flex flex-col items-center px-1">
                  <div className="h-0.5 w-6 bg-brand-200 rounded-full" />
                  <span className="text-brand-300 text-xs">›</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {LEDGER_SECTIONS.map(sec => {
        const items = expenses.filter(e => e.status === sec.key)
        if (!items.length) return null
        const total = items.reduce((s, e) => s + e.total_amount, 0)
        return (
          <div key={sec.key} className={`mb-4 rounded-2xl border ${sec.border} overflow-hidden`}>
            <div className={`${sec.bg} px-4 py-3 flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <span>{sec.icon}</span>
                <span className={`font-bold text-sm ${sec.color}`}>{sec.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full bg-white/60 ${sec.color} font-semibold`}>{items.length}</span>
              </div>
              <span className={`font-bold text-sm ${sec.color}`}>{fmtMoney(total)}</span>
            </div>
            {items.map((e, idx) => {
              const cat         = CAT_MAP[e.category] || CAT_MAP.other
              const isMyExpense = e.submitted_by === currentUserId
              const relevant    = isMyExpense
                ? e.total_amount * (100 - e.payer_pct) / 100
                : e.total_amount * e.payer_pct / 100
              return (
                <button key={e.id} onClick={() => onSelectExpense(e)}
                  className={`w-full px-4 py-3 bg-white flex items-center gap-3 text-left active:bg-slate-50 ${
                    idx < items.length - 1 ? 'border-b border-slate-100' : ''
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sec.dot}`} />
                  <span className="text-xl flex-shrink-0">{cat.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{e.title}</p>
                    <p className="text-xs text-slate-500">
                      {fmtDate(e.purchase_date)} · {isMyExpense ? 'You submitted' : 'Co-Parent submitted'}
                      {e.payment_method ? ` · ${e.payment_method}` : ''}
                      {e.receipt_url ? ' · 📎' : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-slate-800">{fmtMoney(e.total_amount)}</p>
                    <p className="text-xs text-slate-500">{isMyExpense ? 'They owe' : 'You owe'} {fmtMoney(relevant)}</p>
                  </div>
                </button>
              )
            })}
          </div>
        )
      })}

      {expenses.length === 0 && (
        <div className="text-center py-16">
          <span className="text-5xl">📊</span>
          <p className="text-slate-600 font-semibold mt-4">No expenses yet</p>
        </div>
      )}
    </div>
  )
}

// ── Filter Tabs ───────────────────────────────────────────────────────────────
const FILTERS = [
  { id: 'all',      label: 'All',       icon: '📋' },
  { id: 'pending',  label: 'Review',    icon: '📤' },
  { id: 'approved', label: 'Approved',  icon: '✅' },
  { id: 'paid',     label: 'Paid',      icon: '💰' },
  { id: 'rejected', label: 'Declined',  icon: '🚫' },
]

// ── Main Screen ───────────────────────────────────────────────────────────────
export default function ExpenseLog() {
  const navigate = useNavigate()
  const { user } = useApp()
  const [expenses, setExpenses]         = useState([])
  const [loading, setLoading]           = useState(true)
  const [showAdd, setShowAdd]           = useState(false)
  const [selected, setSelected]         = useState(null)
  const [filter, setFilter]             = useState('all')
  const [search, setSearch]             = useState('')
  const [viewMode, setViewMode]         = useState('log')
  const [quickPayConfirm, setQuickPayConfirm] = useState(null) // { method, expense, amount }

  const load = useCallback(async () => {
    try {
      const d = await api.getExpenses()
      setExpenses(d.expenses || [])
    } catch {}
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  async function handleAdd(body) {
    await api.postExpense(body)
    await load()
  }

  async function handleAction(id, action, note) {
    await api.actionExpense(id, action, undefined, note)
    setSelected(null)
    await load()
  }

  async function handleMarkPaid(id, method) {
    await api.actionExpense(id, 'paid', method)
    setSelected(null)
    await load()
  }

  // Quick-pay: single expense directly from the card (no detail sheet needed)
  async function handleQuickPayApple(expense) {
    const amount = expense.total_amount * expense.payer_pct / 100
    if (window.PaymentRequest) {
      try {
        const req = new window.PaymentRequest(
          [{ supportedMethods: 'https://apple.com/apple-pay',
             data: { version: 3, merchantIdentifier: 'merchant.goodguardian',
                     merchantCapabilities: ['supports3DS'],
                     supportedNetworks: ['visa','masterCard','amex','discover'],
                     countryCode: 'US' } }],
          { total: { label: `GoodGuardian: ${expense.title}`,
                     amount: { currency: 'USD', value: amount.toFixed(2) } } }
        )
        if (await req.canMakePayment()) {
          const result = await req.show()
          await result.complete('success')
          await handleMarkPaid(expense.id, 'Apple Pay')
          return
        }
      } catch (err) { if (err?.name === 'AbortError') return }
    }
    setQuickPayConfirm({ method: 'Apple Pay', expense, amount })
  }

  function handleQuickPayVenmo(expense) {
    const amount = expense.total_amount * expense.payer_pct / 100
    const note   = encodeURIComponent(`GoodGuardian: ${expense.title}`)
    const a      = document.createElement('a')
    a.href = `venmo://paycharge?txn=pay&amount=${amount.toFixed(2)}&note=${note}`
    a.click()
    setTimeout(() => setQuickPayConfirm({ method: 'Venmo', expense, amount }), 1800)
  }

  const owedExpenses = expenses.filter(e => e.status === 'approved' && e.submitted_by !== user?.id)

  const filtered    = expenses.filter(e => {
    if (filter !== 'all' && e.status !== filter) return false
    if (search) {
      const q = search.toLowerCase()
      return e.title.toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        (e.notes || '').toLowerCase().includes(q)
    }
    return true
  })
  const grouped     = groupByDate(filtered)
  const reviewCount = expenses.filter(e => e.status === 'pending' && e.submitted_by !== user?.id).length

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <AppHeader title="Expense Log" showBack onBack={() => navigate('/dashboard')}
        right={expenses.length > 0 ? (
          <button
            onClick={() => printExpenseLog({ userName: user?.name, state: user?.state, expenses, currentUserId: user?.id })}
            className="tap-target text-white/80" aria-label="Export PDF"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
        ) : null}
      />

      {/* View toggle + search */}
      <div className="px-4 pt-3 pb-2 bg-white border-b border-slate-100 flex-shrink-0 space-y-2">
        <div className="flex bg-slate-100 rounded-xl p-0.5">
          <button onClick={() => setViewMode('log')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'log' ? 'bg-white shadow text-brand-700' : 'text-slate-500'}`}
          >📋 Log</button>
          <button onClick={() => setViewMode('ledger')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'ledger' ? 'bg-white shadow text-brand-700' : 'text-slate-500'}`}
          >📊 Ledger</button>
        </div>
        {viewMode === 'log' && (
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-slate-400 flex-shrink-0">
              <circle cx={11} cy={11} r={8} /><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search expenses…"
              className="flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder-slate-400"
            />
          </div>
        )}
      </div>

      {expenses.length > 0 && <SummaryBar expenses={expenses} currentUserId={user?.id} />}

      {/* Owed banner — shown in log view when co-parent has approved expenses you haven't paid */}
      {viewMode === 'log' && owedExpenses.length > 0 && (
        <OwedBanner owedExpenses={owedExpenses} onMarkPaid={handleMarkPaid} />
      )}

      {/* Filter tabs */}
      {viewMode === 'log' && (
        <div className="flex gap-1.5 px-4 py-2.5 overflow-x-auto no-scrollbar bg-white border-b border-slate-100 flex-shrink-0">
          {FILTERS.map(f => {
            const count = f.id === 'pending' && reviewCount > 0 ? reviewCount : null
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                  filter === f.id ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-600'
                }`}
              >
                <span>{f.icon}</span>
                <span>{f.label}</span>
                {count && (
                  <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${filter === f.id ? 'bg-white/30' : 'bg-red-500 text-white'}`}>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {viewMode === 'ledger' && (
        <LedgerView expenses={expenses} currentUserId={user?.id} onSelectExpense={setSelected} />
      )}

      {viewMode === 'log' && (
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {loading && <div className="text-center py-16 text-slate-500 text-sm">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 px-6">
              <span className="text-5xl">{filter === 'all' ? '🧾' : FILTERS.find(f => f.id === filter)?.icon}</span>
              <p className="text-slate-600 font-semibold mt-4">
                {filter === 'all' ? 'No expenses yet' : `No ${FILTERS.find(f => f.id === filter)?.label.toLowerCase()} expenses`}
              </p>
              <p className="text-slate-500 text-sm mt-1">
                {filter === 'all'
                  ? 'Log a shared expense and your co-parent will be notified to review it.'
                  : `Expenses that have been ${FILTERS.find(f => f.id === filter)?.label.toLowerCase()} will appear here.`}
              </p>
              {filter === 'all' && (
                <button onClick={() => setShowAdd(true)}
                  className="mt-5 px-6 py-2.5 rounded-full bg-brand-700 text-white text-sm font-bold"
                >+ Log First Expense</button>
              )}
            </div>
          )}
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <div className="px-4 py-2 bg-slate-100">
                <p className="text-xs font-semibold text-slate-500">{date}</p>
              </div>
              {items.map(e => (
                <ExpenseCard key={e.id} expense={e} currentUserId={user?.id}
                  onClick={() => setSelected(e)}
                  onQuickPayApple={handleQuickPayApple}
                  onQuickPayVenmo={handleQuickPayVenmo}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* FAB */}
      <button onClick={() => setShowAdd(true)}
        className="fixed bottom-20 right-5 w-14 h-14 rounded-full bg-brand-700 text-white shadow-xl flex items-center justify-center text-2xl font-bold z-30 active:scale-95 transition-transform"
      >+</button>

      {showAdd && <AddExpenseModal onClose={() => setShowAdd(false)} onSave={handleAdd} />}
      {selected && (
        <ExpenseDetail
          expense={selected}
          currentUserId={user?.id}
          onClose={() => setSelected(null)}
          onAction={handleAction}
          onMarkPaid={handleMarkPaid}
        />
      )}

      {/* Quick-pay confirmation overlay (single expense, triggered from card) */}
      {quickPayConfirm && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40"
          onClick={() => setQuickPayConfirm(null)}>
          <div className="w-full bg-white rounded-t-2xl shadow-xl px-5 pt-5 pb-10"
            onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />
            <p className="text-base font-bold text-slate-800 mb-1">
              Payment sent via {quickPayConfirm.method}?
            </p>
            <p className="text-sm text-slate-500 mb-5">
              {fmtMoney(quickPayConfirm.amount)} for <strong>{quickPayConfirm.expense.title}</strong> —
              confirm to mark this expense as paid.
            </p>
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  await handleMarkPaid(quickPayConfirm.expense.id, quickPayConfirm.method)
                  setQuickPayConfirm(null)
                }}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white font-bold text-sm"
              >✓ Yes, Mark Paid</button>
              <button
                onClick={() => setQuickPayConfirm(null)}
                className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium text-sm"
              >Not Yet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
